import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useCreateAttorneyMutation } from '@/services/api/attorneysService';

type Props = {
  firmId: string;
  onCreated?: (id: string) => void;
  triggerLabel?: string;
  disabled?: boolean;
};

export const AddAttorneyDialog = ({
  firmId,
  onCreated,
  triggerLabel = 'Add Attorney',
  disabled,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [barNumber, setBarNumber] = useState('');

  const [createAttorney, { isLoading }] = useCreateAttorneyMutation();

  const handleSave = async () => {
    if (!firmId) {
      toast.error('Please select a firm first');
      return;
    }
    try {
      const created = await createAttorney({
        firm_id: firmId,
        body: {
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          bar_number: barNumber || null,
        },
      }).unwrap();
      toast.success('Attorney created');
      setOpen(false);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setBarNumber('');
      if (onCreated) onCreated(created.id);
    } catch (e: any) {
      const msg = e?.data?.detail || 'Failed to create attorney';
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          disabled={disabled}
          className="h-auto p-0 text-xs text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
        >
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Attorney</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field>
            <Label htmlFor="atty_first_name">First Name</Label>
            <Input
              id="atty_first_name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Field>
          <Field>
            <Label htmlFor="atty_last_name">Last Name</Label>
            <Input
              id="atty_last_name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Field>
          <Field>
            <Label htmlFor="atty_email">Email</Label>
            <Input
              id="atty_email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field>
            <Label htmlFor="atty_phone">Phone</Label>
            <Input
              id="atty_phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Field>
          <Field>
            <Label htmlFor="atty_bar_number">Bar/Juris Number</Label>
            <Input
              id="atty_bar_number"
              value={barNumber}
              onChange={(e) => setBarNumber(e.target.value)}
              placeholder="Optional"
            />
          </Field>
        </div>
        <DialogFooter className="mt-4">
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || !firstName || !lastName || !email || !phone}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
